import {RefObject, useId} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {FormProvider, useForm} from 'react-hook-form';
import {cn} from '@chekin/guestapp-ui-kit';
import {PATHS} from 'routes/constants';
import {useEsimPaymentsCreatedListener} from './useEsimPaymentsCreatedListener';
import {useSummary} from 'context/summary/useSummary';
import {useFetchEsimPackages} from 'hooks/esim/useFetchEsimPackages';
import {FormTypes} from './types';
import {CURRENCIES, CURRENCIES_SIGNS} from '@chekin/core';
import {useMutateEsimOrders} from 'hooks/esim/useMutateEsimOrders';
import {useReservation} from 'context/reservation/useReservation';
import {groupByDays} from './groupByDays';
import {usePath} from 'hooks';
import {buildCreateEsimOrderPayload} from './buildCreateEsimOrderPayload';
import {TotalToPay} from '../TotalToPay/TotalToPay';
import {PackageCheckbox} from './PackageCheckbox';
import {DeviceCompatibilityModal} from '../DeviceCompatibilityModal/DeviceCompatibilityModal';

type Props = {
  className?: string;
  bottomPanelRef?: RefObject<HTMLDivElement>;
};
export const ChooseForm = ({className, bottomPanelRef}: Props) => {
  const formId = useId();
  const {t} = useTranslation();
  const {getFullPath} = usePath();
  const {data: summary} = useSummary();
  const {data: reservation, housingId, reservationId} = useReservation();
  const currency =
    reservation?.airalo_settings?.currency?.toLocaleLowerCase() as CURRENCIES;
  const country = summary?.location?.country?.code;
  const {data: esimPackages, isLoading: isLoadingEsimPackages} = useFetchEsimPackages({
    country,
    settings: reservation?.airalo_settings,
  });
  const navigate = useNavigate();
  const {mutateAsync: mutateEsimOrders, isPending: isMutatingEsimOrders} =
    useMutateEsimOrders();

  const groupedEsimPackages = groupByDays(esimPackages?.results);

  const methods = useForm<FormTypes>({
    shouldUnregister: true,
  });

  const {waitForPaymentsCreated} = useEsimPaymentsCreatedListener();

  const submitHandler = async (data: FormTypes) => {
    const payload = buildCreateEsimOrderPayload({
      data,
      reservationId: reservationId || '',
      housingId: housingId || '',
      airaloSettings: reservation?.airalo_settings,
    });
    const isPaymentsCreatedPromise = waitForPaymentsCreated(payload);
    await mutateEsimOrders(payload);

    try {
      await isPaymentsCreatedPromise;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    } finally {
      navigate(getFullPath(PATHS.payments.main), {
        state: {from: getFullPath(PATHS.esim.choosePlan)},
      });
    }
  };

  const currencySign = currency ? CURRENCIES_SIGNS[currency] : '';

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        className={cn('flex flex-col gap-6 flex-1 basis-96', className)}
        onSubmit={methods.handleSubmit(submitHandler)}
      >
        <div>
          <h2 className="text-xl font-medium">{t('esim.choose_plan.title')}</h2>
          <DeviceCompatibilityModal />
        </div>
        {groupedEsimPackages.map(group => (
          <fieldset key={group.days} className="flex flex-col gap-4">
            <legend className="text-sm font-semibold uppercase mb-4 text-[var(--font-secondary)]">
              {t('esim.choose_plan.days', {days: group.days})}
            </legend>
            {group.packages.map(esimPackage => (
              <PackageCheckbox
                key={esimPackage.package_id}
                esimPackage={esimPackage}
                currencySign={currencySign}
              />
            ))}
          </fieldset>
        ))}
      </form>
      <TotalToPay
        bottomPanelRef={bottomPanelRef}
        currencySign={currencySign}
        isLoading={isMutatingEsimOrders || isLoadingEsimPackages}
        esimPackages={esimPackages?.results || []}
        className="flex-1 basis-96"
        formId={formId}
      />
    </FormProvider>
  );
};
